import { DateRange } from "@blueprintjs/datetime";
import { saveAs } from "file-saver";
import { applySnapshot, getSnapshot, types } from "mobx-state-tree";
import { DATE_FORMAT, DEFAULT_SORTING_ASCENDING_VALUE } from "modules/common/constants";
import { AgentsDictionary, initialState as agentsState } from "modules/common/models/agents-dictionary";
import { flow } from "modules/common/models/flow";
import { Notificator } from "modules/common/models/notificator";
import { Queryable } from "modules/common/models/queryable";
import { TableSorter } from "modules/common/models/table-sorter";
import { Transport } from "modules/common/models/transport";
import { buildCacheStorage } from "modules/common/services/cache";
import { apiUrls } from "modules/common/services/communication/urls";
import { base64ToBlob, printPdf } from "modules/common/services/files";
import { formatDate } from "modules/common/services/formatting/date";
import { toJsonHard } from "modules/common/services/mobx/serialize";
import { toString } from "modules/common/services/strings";
import { getSortOption, SortOptions } from "modules/common/services/table/sorting-storage";
import { texts } from "modules/common/texts";
import {
    initialState as typesState,
    OverheadTypeDictionary,
} from "modules/dictionaries/overhead-types/models/overhead-type-dictionary";
import {
    FinanceRequestDictionary,
    initialState as requestsState,
} from "modules/expenses/finance-requests/models/finance-requests-dictionary";
import { initialState as ordersState, OrderDictionary } from "modules/orders-manage/models/order-dictionary";
import moment from "moment";
import { OtherOrderSpendingType, OutsourcedOrderSpendingType } from "../../../orders-manage/models/order-spending";
import { fields, SpendingsListRow, SpendingsListRowSnapshotType, SpendingsListRowType } from "./spending-list-row";

export const cache = buildCacheStorage("SpendingsList");
const FILTERS_KEY = "filters";

/** Период по умолчанию — последние 3 месяца, как подставляет сервер при пустых датах */
export const defaultPeriod = (): [Date, Date] => {
    const end = moment().startOf("day");
    return [end.clone().subtract(3, "months").toDate(), end.toDate()];
};

const Filters = types
    .model({
        orderId: types.maybeNull(types.string),
        agentId: types.maybeNull(types.string),
        isIncoming: types.maybeNull(types.boolean),
        isProduction: types.maybeNull(types.boolean),
        start: types.maybeNull(types.Date),
        end: types.maybeNull(types.Date),
        from: types.maybeNull(types.string),
        to: types.maybeNull(types.string),
        queryString: types.maybeNull(types.string),
        spendingId: types.maybeNull(types.string),
    })
    .views((self) => ({
        get fromAsNumber() {
            const number = parseFloat(self.from || "");
            return isNaN(number) ? null : number;
        },

        get toAsNumber() {
            const number = parseFloat(self.to || "");
            return isNaN(number) ? null : number;
        },
    }))
    .views((self) => ({
        get range(): DateRange {
            return [self.start || null, self.end || null];
        },

        get dateRangeIsEmpty() {
            return !self.end || !self.start;
        },

        get sumRangeNotEmpty() {
            return self.fromAsNumber !== null || self.toAsNumber !== null;
        },
    }))
    .actions((self) => ({
        serialize(forRequest: boolean) {
            return {
                ...getSnapshot(self),
                start: forRequest ? formatDate(self.start) : null,
                end: forRequest ? formatDate(self.end) : null,
                to: self.toAsNumber,
                from: self.fromAsNumber,
            };
        },
    }));

export const SpendingsList = types
    .compose(
        Notificator,
        Transport,
        Queryable,
        types.model({
            message: types.string,
            rows: types.array(SpendingsListRow),
            orders: OrderDictionary,
            agents: AgentsDictionary,
            overheadTypes: OverheadTypeDictionary,
            requests: FinanceRequestDictionary,
            sorter: TableSorter,
            filters: Filters,
            loader: types.number,
            incomingSum: types.number,
            spendingSum: types.number,
        })
    )
    .views((self) => ({
        get asMap(): TStringMap<SpendingsListRowType> {
            return self.rows.reduce((result, row) => {
                result[row.id] = row;
                return result;
            }, {} as TStringMap<SpendingsListRowType>);
        },

        get data() {
            // строки уже отфильтрованы поиском на сервере
            return self.rows.map(toJsonHard);
        },

        get isEmpty() {
            return self.rows.length <= 1;
        },

        get isLoading() {
            return self.loader > 0;
        },
    }))
    .views((self) => ({
        get spendingSumm() {
            let sum = 0;
            self.data.forEach((item) => {
                sum += item.sum;
            });
            return sum;
        },
    }))
    .actions((self) => {
        // номер последнего запроса списка: ответ на устаревший запрос не применяем,
        // иначе он перетирал таблицу и итоги данными по прежним фильтрам
        let lastRequest = 0;

        return {
            init() {
                self.orders.isEmpty && self.orders.load();
                self.agents.isEmpty && self.agents.load();
                self.overheadTypes.isEmpty && self.overheadTypes.load();
                // одобренные запросы на оплату меняются постоянно — перечитываем при каждом заходе на страницу
                self.requests.load();
            },

            loadTable: flow(function* () {
                self.loader++;
                const requestNumber = ++lastRequest;

                try {
                    // поиск применяется тем, что сейчас написано в поле, вместе с любым фильтром
                    self.filters.queryString = self.query.trim() || null;
                    if (self.filters.dateRangeIsEmpty) {
                        const [start, end] = defaultPeriod();
                        self.filters.start = start;
                        self.filters.end = end;
                    }

                    const request = self.filters.serialize(true);
                    if (!self.filters.spendingId) {
                        cache.set(FILTERS_KEY, request);
                    }

                    const data: SpendingsColletion = yield self.transport.get<any>(apiUrls.spendings.list(), {
                        params: request,
                    });
                    if (requestNumber !== lastRequest) {
                        return false;
                    }

                    // фильтры из ответа сервера не применяем: они перетирали то, что пользователь
                    // успел выбрать, пока шёл запрос
                    self.incomingSum = data.incomingSum;
                    self.spendingSum = data.spendingSum;
                    self.message = data.message;
                    applySnapshot(self.rows, data.rows);

                    return true;
                } catch (er) {
                    self.notify.error(er);
                    return false;
                } finally {
                    self.loader--;
                }
            }),

            loadSpendings: flow(function* (id: string) {
                try {
                    let data: SpendingData = yield self.transport.get<any>(apiUrls.spendings.orderSpendings(id));
                    if (data) {
                    }
                    return data;
                } catch (er) {
                    self.notify.error(er);
                    return false;
                } finally {
                    self.loader--;
                }
            }),
        };
    })
    .actions((self) => ({
        removeRow: flow(function* (id: string, revert: boolean) {
            self.loader++;

            try {
                const message: string = yield self.transport.delete<any>(apiUrls.spendings.delete(id), {
                    data: { revert },
                });

                yield self.loadTable();
                self.requests.load();

                self.notify.success(message || texts.messages.removed);

                return true;
            } catch (er) {
                self.notify.error(er);
                return false;
            } finally {
                self.loader--;
            }
        }),

        saveRow: flow(function* (body: CommitRowModel) {
            self.loader++;

            try {
                body = toJsonHard(body);
                if (!body.sum) {
                    body.sum = 1;
                }

                if (body.id) {
                    yield self.transport.post<any>(apiUrls.spendings.update(body.id), body);
                } else {
                    yield self.transport.put<any>(apiUrls.spendings.create(), body);
                }

                self.notify.success(texts.messages.saved);
                yield self.requests.load();

                self.loadTable();

                return true;
            } catch (er) {
                self.notify.error(er);
                return false;
            } finally {
                self.loader--;
            }
        }),

        export: flow(function* () {
            self.loader++;

            try {
                const file: FileDescription = yield self.transport.post<any>(apiUrls.spendings.export, {
                    ...self.filters.serialize(true),
                    asPdf: false,
                });

                const blob: any = yield base64ToBlob(file.content || "", file.mimeType);
                saveAs(blob, file.name);
            } catch (er) {
                self.notify.error(er);
                return false;
            } finally {
                self.loader--;
            }
        }),

        print: flow(function* () {
            self.loader++;

            try {
                const file: FileDescription = yield self.transport.post<any>(apiUrls.spendings.export, {
                    ...self.filters.serialize(true),
                    asPdf: true,
                });
                const blob: any = yield base64ToBlob(file.content || "", file.mimeType);

                if (blob) {
                    const fileURL = URL.createObjectURL(blob);
                    const printer = printPdf(fileURL, true);
                    if (printer) {
                        printer.onclose = () => URL.revokeObjectURL(fileURL);
                    }
                }
            } finally {
                self.loader--;
            }
        }),
    }))
    .actions((self) => ({
        projectSelected(value: string | null) {
            self.filters.orderId = value;
            self.loadTable();
        },

        agentSelected(value: string | null) {
            self.filters.agentId = value;
            self.loadTable();
        },

        // сервер трактует сочетание так же (GetSpendingsListQuery.PrepareUserInput):
        // доход не делится на виды расхода, а вид расхода бывает только у расхода
        isIncomingSelected(value: string | null) {
            self.filters.isIncoming = value === null ? null : value === "true";
            if (self.filters.isIncoming === true) {
                self.filters.isProduction = null;
            }
            self.loadTable();
        },

        isProductionSelected(value: string | null) {
            self.filters.isProduction = value === null ? null : value === "true";
            if (self.filters.isProduction !== null) {
                self.filters.isIncoming = false;
            }
            self.loadTable();
        },

        /** Пустой период — последние 3 месяца */
        datesChanged(value: DateRange | null) {
            const [start, end] = value && value[0] && value[1] ? value : defaultPeriod();
            self.filters.start = start;
            self.filters.end = end;
            self.loadTable();
        },

        /** Применить поиск; пустая строка — сбросить его */
        queryChanged(value: string) {
            self.setQuery(value || "");
            self.loadTable();
        },

        /** Вернуть запомненные фильтры (например, после перехода по ссылке на конкретную операцию) */
        restoreFilters() {
            const filters = cachedFilters();
            applySnapshot(self.filters, filters);
            self.setQuery(filters.queryString || "");
        },
        fromChanged(value: string) {
            self.filters.from = value;
        },

        toChanged(value: string) {
            self.filters.to = value;
        },

        setupFilter(change: BatchFilterChange) {
            self.filters.orderId = null;
            self.filters.agentId = null;
            self.filters.isIncoming = change.isIncoming === null ? null : change.isIncoming === "true";
            self.filters.isProduction = change.isProduction === null ? null : change.isProduction === "true";
            // как на сервере: у дохода нет вида расхода
            if (self.filters.isIncoming === true) {
                self.filters.isProduction = null;
            }
            self.filters.start = change.period ? change.period[0] || null : null;
            self.filters.end = change.period ? change.period[1] || null : null;
            self.filters.queryString = change.query ? change.query : null;
            self.filters.spendingId = change.spending ? change.spending : null;
            self.setQuery(change.query || "");
        },
    }))
    .named("SpendingsList");

export type FiltersType = typeof Filters.Type;
export type FiltersSnapshotType = typeof Filters.SnapshotType;
export type SpendingsListType = typeof SpendingsList.Type;
export type SpendingsListSnapshotType = typeof SpendingsList.SnapshotType;

export const deserializeFilter = (object: any) => ({
    ...object,
    start: object.start ? moment(object.start, DATE_FORMAT).toDate() : null,
    end: object.end ? moment(object.end, DATE_FORMAT).toDate() : null,
    from: toString(object.from),
    to: toString(object.to),
});

const sortStorage = getSortOption(SpendingsList.name);

/** Запомненные фильтры — читаются из хранилища в момент вызова, а не один раз при загрузке модуля */
export const cachedFilters = (): FiltersSnapshotType => {
    const cached = cache.get<FiltersSnapshotType | null>(FILTERS_KEY, null);
    const [start, end] = defaultPeriod();
    const filters: FiltersSnapshotType = {
        orderId: null,
        agentId: null,
        isIncoming: null,
        isProduction: null,
        from: "",
        to: "",
        queryString: null,
        ...(cached ? deserializeFilter(cached) : {}),
        spendingId: null,
    };

    if (!filters.start || !filters.end) {
        filters.start = start.getTime();
        filters.end = end.getTime();
    }

    return filters;
};

export const initialState = (sortOptions: SortOptions | null = null): SpendingsListSnapshotType => {
    const options = sortOptions || sortStorage({ column: fields.sortableDate, asc: DEFAULT_SORTING_ASCENDING_VALUE });
    const filters = cachedFilters();

    return {
        message: "",
        pureQuery: "",
        query: filters.queryString || "",
        loader: 0,
        incomingSum: 0,
        spendingSum: 0,
        orders: ordersState(),
        agents: agentsState(),
        overheadTypes: typesState(),
        requests: requestsState(),
        rows: [],

        sorter: {
            id: SpendingsList.name,
            tableName: SpendingsList.name,
            column: options.column,
            asc: options.asc,
        },
        filters,
    };
};

export interface BatchFilterChange {
    isIncoming: string | null;
    isProduction: string | null;
    period: DateRange | null;
    query: string | null;
    spending: string | null;
}

interface SpendingsColletion {
    rows: SpendingsListRowSnapshotType[];
    query: typeof Filters.SnapshotType;
    message: string;
    incomingSum: number;
    spendingSum: number;
}

interface CommitRowModel {
    id: string;
    sum: number;
    date: string;
    type: string;
    payloadId: string;
    name: string;
    isCash: boolean;
    comment: string;
}

export interface SpendingData {
    outsourcedOrderSpendins: OutsourcedOrderSpendingType[];
    otherOrderSpendings: OtherOrderSpendingType[];
}
